import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { ThemeProvider } from 'styled-components';
import Sticky from 'react-stickynode';
import { DrawerProvider } from 'common/src/contexts/DrawerContext';
import { saasModernTheme } from 'common/src/theme/saasModern';
import { ResetCSS } from 'common/src/assets/css/style';
import { GlobalStyle, ContentWrapper } from '../containers/sassModern.style';

import BannerSection from '../containers/Banner';
import Navbar from '../containers/Navbar';
import WorkingProcessSection from '../containers/WorkingProcess';
import TextBoxSection from '../containers/TextBox';
import InfoSection from '../containers/Info';
import ComparisonTable from '../containers/ComparisonTable';
import Footer from '../containers/Footer';
import SEO from '../components/seo';
import FaqSection from '../containers/Faq';
import { useTranslation } from 'react-i18next';

const MicrosoftEdgePasswordManager = ({ title, description, pageContext }) => {
  const { t } = useTranslation();

  const edgeFeatures = [
    {
      title: 'Edge Chromium Compatibility',
      alt: 'Edge Chromium Compatibility',
      description:
        "Built for Microsoft Edge's Chromium architecture. Optimized performance on Windows 11, Windows 10, and all platforms supporting modern Edge.",
    },
    {
      title: 'Edge Add-ons Store Available',
      alt: 'Edge Add-ons Store Available',
      description:
        'Available through the Microsoft Edge Add-ons store for easy installation. Built to meet Edge extension standards for security and reliability.',
    },
    {
      title: 'Windows Hello Support',
      alt: 'Windows Hello Support',
      description:
        'Enhanced authentication on Windows devices using biometric login. Unlock your password vault with fingerprint or facial recognition on compatible hardware.',
    },
    {
      title: 'Edge Collections Compatible',
      alt: 'Edge Collections Compatible',
      description:
        'Securely save credentials for sites in your Edge Collections. Organize passwords by project, research topic, or shopping lists alongside your web content.',
    },
    {
      title: 'Microsoft 365 Integration',
      alt: 'Microsoft 365 Integration',
      description:
        'Seamlessly manage credentials for Microsoft 365, Azure, and enterprise Microsoft services. Perfect for organizations using Microsoft ecosystem.',
    },
    {
      title: 'Cross-Device Edge Sync',
      alt: 'Cross-Device Edge Sync',
      description:
        'Use Edge on Windows desktop, Surface devices, Xbox, Android, and iOS with unified password access. Your credentials sync instantly across all Microsoft platforms.',
    },
  ];

  const edgeFaqData = [
    {
      title: 'Is Psono compatible with Microsoft Edge?',
      description:
        'Psono offers full-featured password management for Microsoft Edge users at zero cost. The extension is built on auditable open-source code, providing transparency for security-conscious users. Self-hosting capabilities give organizations complete infrastructure control. With NaCl cryptographic protection, seamless multi-device sync, and no subscription requirements, Psono delivers a cost-effective solution for Edge users.',
      expend: false,
    },
    {
      title: 'How do I install Psono on Edge?',
      description:
        'Create your Psono account at psono.pw and verify your email address. Launch Edge and visit the Microsoft Edge Add-ons store to install the Psono extension. The Psono icon will appear in your browser toolbar after installation. Sign in through the extension with your credentials, and Psono will automatically begin securing your passwords while maintaining synchronization across devices.',
      expend: false,
    },
    {
      title: 'Which password manager is best for Edge?',
      description:
        "Edge users seeking robust password management without recurring fees benefit from Psono's approach. The extension provides transparent, open-source code that undergoes community security review. After installing from the Edge Add-ons store, Psono operates seamlessly in the background—capturing credentials, creating strong passwords, and synchronizing your encrypted vault across all platforms.",
      expend: false,
    },
    {
      title: 'Can I export data from Psono?',
      description:
        'Psono ensures complete data ownership through its export capability. Log into the web application, navigate to "Other", and select "Export". Choose your datastore and output format (JSON or CSV). The resulting download includes all vault items you can access, giving you full control over your password data.',
      expend: false,
    },
    {
      title: 'How do I migrate Edge passwords to Psono?',
      description:
        'Export your Edge passwords first: Open Edge Settings > Profiles > Passwords, click the menu (three dots) next to "Saved passwords", and select "Export passwords" to save a CSV file. In Psono\'s web interface, go to "Other" > "Import", select "Chrome CSV" as the format (Edge uses a compatible structure), upload your file, and confirm the import. Your Edge passwords will transfer into Psono with full encryption and cross-device availability.',
      expend: false,
    },
  ];

  const comparisonFeatures = [
    {
      title: 'Advanced Encryption',
      description:
        'Protect passwords with NaCl cryptography. Vault data is encrypted client-side before it is sent to the server.',
      psonoValue: true,
      edgeValue: '-',
    },
    {
      title: 'Open Source & Auditable',
      description:
        'Publicly available source code allows security experts worldwide to verify security claims. Regular third-party audits ensure continued protection.',
      psonoValue: true,
      edgeValue: '-',
    },
    {
      title: 'Advanced Two-Factor Authentication',
      description:
        'Secure your vault with TOTP authenticators, hardware security keys, or FIDO2 WebAuthn credentials for enhanced protection.',
      psonoValue: true,
      edgeValue: '-',
    },
    {
      title: 'Password Generator',
      description:
        'Create long, complex, and unique passwords for every site with customizable options for maximum security.',
      psonoValue: true,
      edgeValue: '-',
    },
    {
      title: 'Self-Hosting Option',
      description:
        'Deploy on your own infrastructure for complete control over your data and compliance with regulatory requirements.',
      psonoValue: true,
      edgeValue: '-',
    },
    {
      title: 'Cross-Platform Sync',
      description:
        'Access your passwords on any browser, mobile device, or desktop OS with automatic synchronization across all devices.',
      psonoValue: true,
      edgeValue: 'Limited',
    },
  ];

  return (
    <ThemeProvider theme={saasModernTheme}>
      <Fragment>
        <SEO
          title={t('Best Free Password Manager Microsoft Edge | Psono')}
          lang={pageContext.lang}
          titleTemplate={`%s`}
          description={t(
            'Secure password manager extension for Microsoft Edge. Store unlimited passwords, sync across devices, and protect your data with military-grade encryption. Free open-source solution.'
          )}
          keywords={[
            'password manager edge',
            'edge password extension',
            'password manager microsoft edge',
            'best free password manager for edge',
            'microsoft edge password manager',
            'edge extension password manager',
            'secure password manager edge',
            'edge password vault',
            'browser password manager edge',
          ]}
        />

        <ResetCSS />
        <GlobalStyle />

        <ContentWrapper>
          <Sticky top={0} innerZ={9999} activeClass="sticky-nav-active">
            <DrawerProvider>
              <Navbar pageContext={pageContext} />
            </DrawerProvider>
          </Sticky>
          <BannerSection
            pageContext={pageContext}
            topText={t('Open-source password security for Edge')}
            titleText={t('Microsoft Edge Password Manager')}
            descriptionText={t(
              'Secure password manager for Microsoft Edge. Free Edge password extension with unlimited storage, advanced encryption, and cross-platform sync.'
            )}
            primeLink={
              'https://microsoftedge.microsoft.com/addons/detail/psono-free-password-man/abobmepfpbkapdlmfhnnkebcnhgeccbm'
            }
            primeText={t('INSTALL PSONO FOR EDGE NOW')}
            downloadLink={`${pageContext.langPathPrefix}/download`}
            downloadText={t('VIEW ALL DOWNLOAD OPTIONS')}
          />
          <TextBoxSection
            description={t(
              'Professional password management for Microsoft Edge'
            )}
            text={t(
              'Psono delivers open-source password security designed specifically for Edge users on Windows, Mac, and Linux. Access unlimited password storage, real-time synchronization across platforms, and enterprise-grade security features—all included in a permanently free tier with no feature limitations.'
            )}
          />
          <ComparisonTable
            features={comparisonFeatures.map((f) => ({
              ...f,
              chromeValue: f.edgeValue,
            }))}
            browserName="Edge"
            title={t(
              'Top reasons to choose Psono over Microsoft Edge native password manager'
            )}
          />
          <TextBoxSection
            description={t('Comprehensive security beyond Edge defaults')}
            text={t(
              "Edge's native password features provide basic credential storage, yet enterprise-grade security demands more robust protection. True password security stems from cryptographic uniqueness and sufficient complexity—requirements that manual password creation rarely meets. Psono generates provably random passwords using cryptographic algorithms for each service, implements NaCl encryption locally before any storage occurs, and offers configurable multi-factor authentication for vault protection. The Edge extension delivers native toolbar integration for rapid credential access while preserving encryption integrity during both storage operations and cross-device synchronization."
            )}
          />
          <WorkingProcessSection
            title={t('Why choose Psono as your Edge password manager?')}
            description={t(
              'Key features that make Psono stand out for Edge users'
            )}
            buttonUrl="/features-for-users"
            items={edgeFeatures}
            pageContext={pageContext}
          />
          <TextBoxSection
            description={t('Edge installation and setup')}
            text={t(
              'Deploying Psono on Edge requires minimal setup time: Create your Psono account and complete email verification. Visit the Microsoft Edge Add-ons store to install the extension—the Psono icon appears in your toolbar immediately after installation. Authenticate through the extension using your credentials. Once active, Psono prompts you to store credentials securely as you encounter login forms. Subsequent visits to saved sites enable instant autofill via the toolbar icon. Your encrypted vault synchronizes continuously across Windows, macOS, Linux, iOS, Android, and other browsers.'
            )}
          />
          <InfoSection
            pageContext={pageContext}
            titleContent={t('Edge Extension Security')}
            descriptionContent={t(
              'The Psono Edge extension implements industry-standard NaCl cryptography for client-side encryption, securing vault data before network transmission. Our open-source codebase receives continuous evaluation from the international security community, providing transparency Edge users value. Whether protecting personal credentials or managing corporate authentication systems, Psono integrates proven encryption directly into your Edge browser workflow.'
            )}
          />
          <FaqSection data={edgeFaqData} />
          <Footer pageContext={pageContext} />
        </ContentWrapper>
      </Fragment>
    </ThemeProvider>
  );
};

MicrosoftEdgePasswordManager.propTypes = {
  title: PropTypes.object,
  description: PropTypes.object,
};

MicrosoftEdgePasswordManager.defaultProps = {
  title: {
    fontSize: ['24px', '32px', '40px', '42px', '46px'],
    fontWeight: '700',
    color: '#fff',
    letterSpacing: '-0.025em',
    mb: ['20px', '25px', '25px', '25px', '25px'],
    lineHeight: '1.2',
    textAlign: 'center',
  },
  description: {
    fontSize: ['15px', '16px', '16px', '16px', '16px'],
    color: '#fff',
    lineHeight: '1.75',
    mb: '0',
    textAlign: 'center',
  },
};

export default MicrosoftEdgePasswordManager;
